// 🤖 ANÁLISES CONTEXTUAIS (por aba)
// Análises específicas de: Evolução, Exames Lab, Marcadores
import { useState } from "react";
import { Paciente } from "../../../types";
import { AnaliseIA } from "../types";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
import {
  gerarPromptEvolucao,
  gerarPromptFuncaoHepatica,
  gerarPromptMarcadores,
} from "../services/promptsMedicos";
import { obterContextoGuidelines } from "../rag";
import { callAnalyze } from "../../../lib/analyzeClient";
import {
  derivarStatusGeral,
  derivarGravidade,
  derivarUrgencia,
} from "../services/derivarStatusGeral";

const VERSAO_PROMPT = "3.0-contextual";

/**
 * 📝 Converter texto da IA em objeto AnaliseIA
 */
const criarAnaliseDeTexto = (texto: string): AnaliseIA => {
  const textoLimpo = texto
    .replace(/```json\n?/g, "")
    .replace(/```\n?/g, "")
    .trim();

  // B261 (AI_117): fonte única, robusta à formatação do modelo.
  const statusGeral = derivarStatusGeral(textoLimpo);
  // B275 (AI_120) — eixos separados p/ o selo duplo (Gravidade × Urgência).
  const gravidade = derivarGravidade(textoLimpo);
  const urgencia = derivarUrgencia(textoLimpo);

  return {
    id: "",
    pacienteId: "",
    dataAnalise: new Date().toISOString(),
    versao: VERSAO_PROMPT,
    resumo: {
      statusGeral,
      gravidade,
      urgencia,
      principaisAchados: [textoLimpo],
      alertasCriticos: [],
    },
    analises: {
      hepatica: {
        status: "normal",
        enzimas: {
          ast: { valorAtual: 0, status: "normal" },
          alt: { valorAtual: 0, status: "normal" },
          ggt: { valorAtual: 0, status: "normal" },
          fa: { valorAtual: 0, status: "normal" },
        },
        bilirrubinas: {
          total: { valorAtual: 0, status: "normal" },
          direta: { valorAtual: 0, status: "normal" },
          indireta: { valorAtual: 0, status: "normal" },
        },
        sinteseHepatica: {
          albumina: { valorAtual: 0, status: "normal" },
          tap: { valorAtual: 0, status: "normal" },
          inr: { valorAtual: 0, status: "normal" },
        },
        interpretacao: textoLimpo,
      },
      renal: {
        status: "normal",
        creatinina: { valorAtual: 0, status: "normal" },
        ureia: { valorAtual: 0, status: "normal" },
        interpretacao: "",
      },
      imunossupressao: {
        status: "adequado",
        interpretacao: "",
      },
    },
    sugestoes: [],
    tendencias: [],
    correlacoes: [],
  };
};

/**
 * 🤖 Chamada genérica à API com tratamento de resposta texto/JSON
 * B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
 * Interface preservada bit-a-bit: (prompt: string) => Promise<string>.
 * Os 6 callers internos continuam funcionando sem mudança.
 * Erros (auth, timeout, 500) propagam via throw para os try/catch
 * dos callers — comportamento idêntico ao pré-patch.
 */
const chamarAPI = async (prompt: string): Promise<string> => {
  const data = await callAnalyze<{
    content: Array<{ type: string; text: string }>;
  }>({ prompt, useThinking: true });

  // Extrair texto da resposta (ignorar blocos de thinking)
  const texto = data.content
    .filter((c: any) => c.type === "text")
    .map((c: any) => c.text)
    .join("\n");

  return texto.trim();
};

export function useAnaliseContextual() {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /**
   * 📋 Analisar evolução/consulta específica
   */
  const analisarEvolucao = async (
    paciente: Paciente,
    evolucaoId: string
  ): Promise<AnaliseIA | null> => {
    try {
      setLoading(true);
      setErro(null);
      console.log("📋 Analisando evolução clínica...");

      const dadosAnonimizados = await coletarDadosCompletos(paciente);

      let contextoGuidelines = "";
      try {
        contextoGuidelines = await obterContextoGuidelines(dadosAnonimizados);
      } catch (e) {
        console.warn("⚠️ RAG indisponível");
      }

      const promptBase = gerarPromptEvolucao(dadosAnonimizados);
      const prompt = promptBase + contextoGuidelines;
      const texto = await chamarAPI(prompt);

      console.log("✅ Análise de evolução concluída!");
      return criarAnaliseDeTexto(texto);
    } catch (error: any) {
      console.error("❌ Erro ao analisar evolução:", error);
      setErro(error.message || "Falha ao processar análise");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🧪 Analisar exames laboratoriais específicos
   */
  const analisarExamesLab = async (
    paciente: Paciente,
    exameId: string
  ): Promise<AnaliseIA | null> => {
    try {
      setLoading(true);
      setErro(null);
      console.log("🧪 Analisando exames laboratoriais...");

      const dadosAnonimizados = await coletarDadosCompletos(paciente);

      let contextoGuidelines = "";
      try {
        contextoGuidelines = await obterContextoGuidelines(dadosAnonimizados);
      } catch (e) {
        console.warn("⚠️ RAG indisponível");
      }

      const promptBase = gerarPromptFuncaoHepatica(dadosAnonimizados);
      const prompt = promptBase + contextoGuidelines;
      const texto = await chamarAPI(prompt);

      console.log("✅ Análise de exames concluída!");
      return criarAnaliseDeTexto(texto);
    } catch (error: any) {
      console.error("❌ Erro ao analisar exames:", error);
      setErro(error.message || "Falha ao processar análise");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🔬 Analisar marcadores tumorais
   */
  const analisarMarcadores = async (
    paciente: Paciente,
    marcadorId: string
  ): Promise<AnaliseIA | null> => {
    try {
      setLoading(true);
      setErro(null);
      console.log("🔬 Analisando marcadores tumorais...");

      const dadosAnonimizados = await coletarDadosCompletos(paciente);

      let contextoGuidelines = "";
      try {
        contextoGuidelines = await obterContextoGuidelines(dadosAnonimizados);
      } catch (e) {
        console.warn("⚠️ RAG indisponível");
      }

      const promptBase = gerarPromptMarcadores(dadosAnonimizados);
      const prompt = promptBase + contextoGuidelines;
      const texto = await chamarAPI(prompt);

      console.log("✅ Análise de marcadores concluída!");
      return criarAnaliseDeTexto(texto);
    } catch (error: any) {
      console.error("❌ Erro ao analisar marcadores:", error);
      setErro(error.message || "Falha ao processar análise");
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    erro,
    analisarEvolucao,
    analisarExamesLab,
    analisarMarcadores,
  };
}
